import { AI_GRADIENT_SMALL_IMAGE_URL } from "@/components/common/ai-assets/ai-gradients";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { usePanoCameraTransform } from "@/utils/camera-transform";
import { useSvg } from "@faro-lotv/app-component-toolbox";
import type { IElementImg360 } from "@faro-lotv/ielement-types";
import type { CachedWorldTransform } from "@faro-lotv/project-source";
import type { BoundingBox } from "@faro-lotv/service-wires";
import { useMemo, useRef, type JSX } from "react";
import type { Mesh } from "three";
import { aiFindingColor } from "./ai-finding-color";
import {
  selectBoundingBoxesOfElement,
  selectFocusedBoundingBoxId,
  selectShowBoundingBoxes,
} from "./ai-selectors";
import {
  boundingBoxCenterWorld,
  boundingBoxTopLeftLocal,
} from "./bounding-box-geometry";
import { BoundingBoxLabel } from "./bounding-box-label";
import { BoundingBoxMesh } from "./bounding-box-mesh";
import {
  boundingRegionsTopLeftLocal,
  isDrawableBoundingRegion,
} from "./bounding-region-geometry";
import { BoundingRegionMesh } from "./bounding-region-mesh";
import { useBoundingBoxAnnotation } from "./use-bounding-box-annotation";

type BoundingBoxRendererProps = {
  /** The panorama image element to render the bounding box on */
  pano: IElementImg360;
};

/**
 * @returns a semi-transparent bounding box overlay on a spherical panorama.
 * The bounding box is rendered as a mesh on the sphere surface using spherical coordinates.
 */
export function BoundingBoxRenderer({
  pano,
}: BoundingBoxRendererProps): JSX.Element | null {
  // Get the camera transformation for positioning the mesh in 3D space
  const transform = usePanoCameraTransform(pano);

  const boundingBoxes = useAppSelector(selectBoundingBoxesOfElement(pano.id));

  // When the "show all" toggle is off, only the box the user focused (e.g. by
  // clicking a finding in the chat) is rendered. When the toggle is on, all
  // boxes for this element are shown.
  const showAll = useAppSelector(selectShowBoundingBoxes);
  const focusedBoundingBoxId = useAppSelector(selectFocusedBoundingBoxId);

  const visibleBoxes = useMemo(
    () =>
      boundingBoxes.filter(
        (box): box is BoundingBox =>
          !!box && (showAll || box.boundingBoxId === focusedBoundingBoxId),
      ),
    [boundingBoxes, showAll, focusedBoundingBoxId],
  );

  if (!visibleBoxes.length) return null;

  return (
    <>
      {visibleBoxes.map((box) => (
        <BoxRenderer key={box.boundingBoxId} box={box} transform={transform} />
      ))}
    </>
  );
}

type BoxRendererProps = {
  box: BoundingBox;
  transform: CachedWorldTransform;
};

function BoxRenderer({ box, transform }: BoxRendererProps): JSX.Element | null {
  const { boundingBox, boundingRegions, type, label, title, elementId } = box;

  // Reference to the rendered mesh, used to capture a screenshot of the box.
  const meshRef = useRef<Mesh>(null);

  const gradient = useSvg(AI_GRADIENT_SMALL_IMAGE_URL);

  const arePolygonsEnabled = useAppSelector(
    selectHasFeature(Features.AiFindingsPolygon),
  );

  // The polygons follow the shape of the finding, the box is the coarse fallback
  // for the findings the backend did not segment.
  const regions = useMemo(() => {
    const drawable = arePolygonsEnabled
      ? boundingRegions?.filter(isDrawableBoundingRegion)
      : undefined;
    return drawable?.length ? drawable : undefined;
  }, [arePolygonsEnabled, boundingRegions]);

  const color = aiFindingColor(type);

  // Top-left corner of the finding on the unit sphere, used as the label anchor.
  const topLeftLocal = useMemo(
    () =>
      regions
        ? boundingRegionsTopLeftLocal(regions)
        : boundingBoxTopLeftLocal(boundingBox),
    [regions, boundingBox],
  );

  // World-space position of the bounding box center, used as the annotation target.
  const centerWorld = useMemo(
    () => boundingBoxCenterWorld(boundingBox, transform),
    [boundingBox, transform],
  );

  const { canWriteAnnotations } = useAnnotationPermissions();

  const { createBboxAnnotation } = useBoundingBoxAnnotation({
    centerWorld,
    elementId,
    label,
    title,
    meshRef,
  });

  // The polygons follow the shape of the finding, the box is the coarse fallback
  // for the findings the backend did not segment.
  let findingMesh: JSX.Element | null = null;
  if (regions) {
    findingMesh = (
      <BoundingRegionMesh regions={regions} color={color} meshRef={meshRef} />
    );
  } else if (gradient) {
    findingMesh = (
      <BoundingBoxMesh
        boundingBox={boundingBox}
        gradient={gradient}
        meshRef={meshRef}
      />
    );
  }

  // The label anchors to the mesh and captures a screenshot of it, so the whole finding waits for
  // the gradient rather than showing a label without its box.
  if (!findingMesh) return null;

  return (
    <group position={transform.position} quaternion={transform.quaternion}>
      {findingMesh}

      <BoundingBoxLabel
        position={topLeftLocal}
        label={label}
        color={regions ? color : undefined}
        onClick={canWriteAnnotations ? createBboxAnnotation : undefined}
        allowAnnotationCreation={canWriteAnnotations}
      />
    </group>
  );
}
